import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Loader2, Send } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminAutomatedEmailByIdQuery,
  useAdminSendAutomatedEmailMutation,
} from '../../../features/admin/hooks/use-admin-api'

export const Route = createFileRoute('/admin/emails/$id')({
  component: Page,
})

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive'> = {
  SENT: 'success',
  PENDING: 'warning',
  FAILED: 'destructive',
}

function formatStatus(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase()
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatEmailType(emailType: string): string {
  return emailType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function isHtml(text: string): boolean {
  return text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')
}

function Page() {
  const { id } = Route.useParams()
  const query = useAdminAutomatedEmailByIdQuery(id)
  const sendMutation = useAdminSendAutomatedEmailMutation(id)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  if (query.isLoading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  if (!query.data?.data)
    return <div className="py-20 text-center text-muted-foreground">Not found</div>

  const email = query.data.data

  const handleSend = () => {
    sendMutation.mutate(
      { path: { id } },
      {
        onSuccess: () => showMessage('success', 'Email sent'),
        onError: () => showMessage('error', 'Failed to send'),
      }
    )
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-lg font-bold">{email.subject}</h1>
          {email.status !== 'SENT' && (
            <CtaButton icon={<Send />} loading={sendMutation.isPending} onClick={handleSend}>
              Send now
            </CtaButton>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <Badge variant="secondary" size="sm">
              {formatEmailType(email.emailType)}
            </Badge>
          </Field>
          <Field label="Status">
            <Badge variant={STATUS_VARIANT[email.status] ?? 'secondary'} size="sm">
              {formatStatus(email.status)}
            </Badge>
          </Field>
          <Field label="Recipient">
            <Link
              to="/admin/users/$id"
              params={{ id: email.user.id }}
              className="text-sm text-orange-500 hover:underline"
            >
              {email.user.name || email.recipientEmail}
            </Link>
            <span className="ml-2 text-sm text-muted-foreground">{email.recipientEmail}</span>
          </Field>
          <Field label="Created">{formatDate(email.createdAt)}</Field>
          {email.sentAt && <Field label="Sent at">{formatDate(email.sentAt)}</Field>}
          {email.externalId && (
            <Field label="External ID">
              <span className="font-mono text-xs">{email.externalId}</span>
            </Field>
          )}
          {email.contextId && (
            <Field label="Context ID">
              <span className="font-mono text-xs">{email.contextId}</span>
            </Field>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-muted-foreground">Body</h3>
          {isHtml(email.body) ? (
            <iframe
              srcDoc={email.body}
              title="Email preview"
              className="w-full rounded-md border border-border"
              style={{ height: 600 }}
              sandbox=""
            />
          ) : (
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-4 text-sm leading-relaxed">
              {email.body}
            </pre>
          )}
        </div>
      </div>

      {message && (
        <div
          className={`fixed bottom-6 right-6 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            message.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">{label}</h3>
      <div className="text-sm">{children}</div>
    </div>
  )
}
